<template>
    <div :class="['entity-row', { 'disabled': isDisabled }]">
        <div class="main">
            <div class="header">
                <div :class="['status-indicator', statusClass]"></div>
                <div class="title" @click="open">{{ title }}</div>
            </div>
            <div class="tags">
                <el-tooltip :content="`${tag.label}: ${tag.value}`" v-for="tag in tags" :key="tag.key" :open-delay="500">
                    <tag :icon="tag.icon" :type="tag.type" no-overflow>{{ tag.value }}</tag>
                </el-tooltip>
            </div>
        </div>
        <div class="side">
            <el-tooltip :content="linked ? $t('unlink') : $t('link')" placement="left" :enterable="false">
                <div :class="['row-button', { 'selected': linked }]" v-if="linkAvailable" @click="toggleLink">link</div>
            </el-tooltip>
            <el-tooltip :content="isCompleted ? $t('re-open') : $t('complete')" placement="left" :enterable="false">
                <div class="row-button" v-if="statusAvailable && statusEditable" @click="toggleStatus" v-loading="statusLoading">{{ isCompleted ? 'undo' : 'check' }}</div>
            </el-tooltip>
        </div>
    </div>
</template>

<script lang="ts">
    import Vue, { PropType } from 'vue';

    import tag from '@/controls/tag.vue';

    import { EntityStatus, EntityTag } from '@/utilities/entities';
    import { NavigationProvider } from '@/utilities/navigation';

    export default Vue.extend({
        name: 'entity-row',
        components: {
            tag
        },
        props: {
            title: String,
            status: Number as PropType<EntityStatus>,
            tags: Array as PropType<EntityTag[]>,
            url: String,
            linked: Boolean,
            statusEditable: Boolean
        },
        data() {
            return {
                statusLoading: false,
            };
        },
        computed: {
            statusClass(): string {
                return EntityStatus[this.status].toLowerCase();
            },
            statusAvailable(): boolean {
                return this.status > EntityStatus.General;
            },
            isCompleted(): boolean {
                return this.status == EntityStatus.Completed;
            },
            isDisabled(): boolean {
                return this.status == EntityStatus.Disabled;
            },
            linkAvailable(): boolean {
                return this.linked !== undefined;
            },
        },
        methods: {
            open() {
                NavigationProvider.go(this.url);
            },
            toggleLink() {
                this.$emit('toggle-link');
            },
            async toggleStatus() {
                this.statusLoading = true;
                await new Promise(r => this.$emit('toggle-status', r));
                this.statusLoading = false;
            },
        }
    });

</script>

<style scoped lang="scss">

    .entity-row {
        display: flex;
        flex-direction: row;
        gap: 8px;

        &.disabled > * {
            opacity: .5;
            user-select: none;
            cursor: not-allowed;
        }

        .main {
            display: flex;
            flex-direction: column;
            gap: 8px;
            min-width: 0;
            flex-grow: 1;

            .header {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 12px;

                .status-indicator {
                    width: 3px;
                    height: 16px;
                    border-radius: 4px;
                    flex-shrink: 0;
                    background-color: var(--grey-600);

                    &.active {
                        background-color: var(--success-600);
                    }

                    &.today {
                        background-color: var(--info-500);
                    }

                    &.overdue {
                        background-color: var(--danger-600);
                    }

                    &.completed {
                        background-color: var(--primary-600);
                    }

                    &.none {
                        display: none;
                    }
                }

                .title {
                    @extend %label-medium;
                    @extend %no-overflow;
                    cursor: pointer;

                    &:hover {
                        color: var(--primary-500);
                    }
                }
            }

            .tags {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 6px;

                .tag {
                    flex-grow: 1;
                    flex-basis: calc(50% - 3px);
                    max-width: calc(50% - 3px);
                    max-width: fit-content;
                }
            }
        }

        .side {
            display: flex;
            flex-direction: column;
            gap: 8px;
            flex-shrink: 0;

            .row-button {
                @extend %md-icon;
                width: 31px;
                height: 32px;
                max-width: none;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 16px;
                border: solid 1px var(--grey-300);
                font-size: 20px;
                color: var(--grey-800);
                overflow: hidden;
                cursor: pointer;

                &:hover {
                    background-color: var(--grey-50);
                }

                &.selected {
                    background-color: var(--primary-100);
                    border-color: var(--primary-100);
                    color: var(--primary-500);

                    &:hover {
                        background-color: var(--button-secondary-hover);
                        border-color: var(--button-secondary-hover);
                    }
                }
            }
        }

        /*&:not(:hover) {
            .row-button:not(.selected) {
                visibility: hidden;
            }
        }*/
    }

</style>